import { type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { NotificationBell } from './NotificationBell';
import { useQuery } from '@tanstack/react-query';
import type { User } from '../lib/types';
import { api } from '../lib/api';

const links = [
  { to: '/dashboard', label: 'หน้าหลัก', icon: '⌂' },
  { to: '/calendar', label: 'ปฏิทิน', icon: '◫' },
  { to: '/calendar/import', label: 'กล่องนำเข้า', icon: '⇩' },
  { to: '/appointments', label: 'รายการ', icon: '▣' },
  { to: '/meetings', label: 'การประชุม', icon: '⌘' },
  { to: '/documents', label: 'คลังเอกสาร', icon: '▤' },
  { to: '/document-search', label: 'ค้นหา', icon: '⌕' },
  { to: '/create', label: 'สร้างรายการ', icon: '⊕' },
  { to: '/settings', label: 'ตั้งค่า', icon: '⚙' },
  { to: '/delegate-calendar', label: 'ลงนัดให้ผู้อื่น', icon: '＋' },
];
const mobileLinks = [
  { to: '/dashboard', label: 'หน้าหลัก', icon: '⌂' },
  { to: '/calendar', label: 'ปฏิทิน', icon: '◫' },
  { to: '/create', label: 'สร้าง', icon: '⊕' },
  { to: '/document-search', label: 'ค้นหา', icon: '⌕' },
  { to: '/more', label: 'เพิ่มเติม', icon: '▦' },
];

function MobileNavigation() {
  const { pathname } = useLocation();
  const primaryPath = mobileLinks.slice(0, 4).some((link) => pathname === link.to);

  return (
    <nav
      className="workspace-mobile-nav safe-bottom fixed inset-x-0 bottom-0 z-40 w-full border-t lg:hidden"
      aria-label="เมนูหลักบนมือถือ"
    >
      <div className="grid w-full grid-cols-5 gap-1 px-1 pt-1">
        {mobileLinks.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end
            className={({ isActive }) =>
              clsx(
                'flex min-h-16 min-w-0 flex-col items-center justify-center gap-0.5 rounded-xl px-1 text-[11px] font-semibold',
                isActive || (link.to === '/more' && !primaryPath)
                  ? 'bg-[#254766] text-[#91dcff]'
                  : 'text-[#b9d1e5]',
              )
            }
          >
            <span className="text-xl" aria-hidden>
              {link.icon}
            </span>
            {link.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function Layout({
  children,
  title,
  wide = false,
}: {
  children: ReactNode;
  title?: string;
  wide?: boolean;
}) {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<{ data: User }>('/api/me').then((response) => response.data) });
  const delegate = me.data?.loginMode === 'delegate';
  return (
    <div className="workspace-shell min-h-screen">
      <header className="workspace-header sticky top-0 z-30 border-b backdrop-blur lg:ml-64">
        <div className={`mx-auto flex h-16 items-center px-4 ${wide ? 'max-w-7xl' : 'max-w-4xl'}`}>
          <img src="/icon.svg" alt="" className="mr-3 h-9 w-9 rounded-xl" />
          <div>
            <p className="text-xs font-medium text-[#8bd8ff]">KW Workspace</p>
            <h1 className="font-semibold text-[#edf7ff]">{title ?? 'ศูนย์จัดการรายการ'}</h1>
          </div>
          <div className="ml-auto">{delegate ? <button className="text-sm text-[#edf7ff]" onClick={async () => { await api('/api/auth/logout', { method: 'POST' }); window.location.assign('/login'); }}>ออกจากระบบ</button> : <NotificationBell />}</div>
        </div>
      </header>
      <aside className="workspace-sidebar fixed inset-y-0 left-0 z-40 hidden w-64 border-r p-4 lg:block">
        <div className="flex items-center gap-3 border-b border-[#355574] pb-5">
          <img src="/icon.svg" alt="" className="h-10 w-10 rounded-xl" />
          <div>
            <p className="text-xs font-medium text-[#8bd8ff]">KW Workspace</p>
            <p className="font-bold text-[#edf7ff]">ศูนย์งานและเอกสาร</p>
          </div>
        </div>
        <nav className="mt-5 space-y-4" aria-label="เมนูหลัก">
          {delegate ? <NavLink to="/delegate-calendar" className="block rounded-xl bg-[#254766] px-3 py-2.5 text-sm font-medium text-[#91dcff]">ตารางนัดผู้ช่วย</NavLink> : <>
          {[
            { title: 'ทำงาน', items: [links[0], links[1], links[7], links[9], links[2], links[3]] },
            { title: 'จัดการ', items: [links[4], links[5], links[6]] },
            { title: 'ระบบ', items: [links[8]] },
          ].map((group) => (
            <div key={group.title}>
              <p className="mb-1 px-3 text-xs font-semibold text-[#94b4d0]">{group.title}</p>
              {group.items.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition',
                      isActive
                        ? 'bg-[#254766] text-[#91dcff]'
                        : 'text-[#b9d1e5] hover:bg-[#223c59]',
                    )
                  }
                >
                  <span className="text-lg" aria-hidden>
                    {link.icon}
                  </span>
                  {link.label}
                </NavLink>
              ))}
            </div>
          ))}
          </>}
        </nav>
        <div className="absolute inset-x-4 bottom-5 rounded-xl bg-[#203650] p-3 text-xs text-[#b9d1e5]">
          ระบบจัดการประชุมและเอกสาร
        </div>
      </aside>
      <main
        className={`workspace-main mx-auto px-4 py-5 pb-28 lg:ml-64 lg:pb-10 ${wide ? 'max-w-7xl' : 'max-w-4xl'}`}
      >
        {children}
      </main>
      {!delegate && <MobileNavigation />}
    </div>
  );
}

export function LoadingScreen({ message = 'กำลังโหลด…' }: { message?: string }) {
  return (
    <div className="mx-auto max-w-lg animate-pulse space-y-4 p-5" aria-live="polite">
      <div className="h-8 w-2/3 rounded bg-slate-200" />
      <div className="h-32 rounded-2xl bg-slate-200" />
      <p className="text-center text-sm text-slate-500">{message}</p>
    </div>
  );
}

export function ErrorNotice({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800"
    >
      {message}
    </div>
  );
}

export function StatusPill({ status }: { status: string }) {
  const labels: Record<string, string> = {
    draft: 'ฉบับร่าง',
    image_uploaded: 'อัปโหลดแล้ว',
    ocr_queued: 'รออ่าน',
    ocr_processing: 'กำลังอ่าน',
    needs_review: 'รอตรวจสอบ',
    ready: 'พร้อมบันทึก',
    calendar_created: 'เพิ่มปฏิทินแล้ว',
    ocr_failed: 'อ่านไม่สำเร็จ',
    planned: 'นัดแล้ว',
    completed: 'เสร็จแล้ว',
    cancelled: 'ยกเลิก',
  };
  const attention = ['needs_review', 'ocr_failed'].includes(status);
  return (
    <span
      className={clsx(
        'rounded-full px-2.5 py-1 text-xs font-semibold',
        attention ? 'bg-amber-100 text-amber-800' : 'bg-teal-50 text-teal-800',
      )}
    >
      {labels[status] ?? status}
    </span>
  );
}
